import type { Dispatch, FC, ReactNode, SetStateAction } from "react";
import type { Theme } from "@mui/material";

import ModalBase from "../ModalBase";
import { Box, Button, Stack, Typography } from "@mui/material";
import NotificationPreview from "../NotificationPreview";
import { BrandingPreview } from "../../screens/Campaigns/CampaignBranding/BrandingPreview";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";

import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@mui/styles";
import CustomButton from "../CustomButton";

interface NotificationModalBaseProps {
  isOpen: boolean;
  previewModalOpen: boolean;
  detectedChange: boolean;
  notificationHtml: string;
  title: string;
  isLoading?: boolean;
  isNewNotification?: boolean;
  setPreviewModalOpen: Dispatch<SetStateAction<boolean>>;
  onClose: () => void;
  onReset: () => void;
  onSave: () => Promise<void>;
  children: ReactNode;
}

export const NotificationModalBase: FC<NotificationModalBaseProps> = ({
  children,
  isOpen,
  previewModalOpen,
  notificationHtml,
  title,
  detectedChange,
  isLoading = false,
  isNewNotification = false,
  onClose,
  onSave,
  onReset,
  setPreviewModalOpen,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <>
      <ModalBase
        isOpen={isOpen}
        onClose={onClose}
        style={{ overflow: "auto" }}
        center
      >
        <Stack
          direction='column'
          bgcolor={theme.palette.natural.t4}
          p={3}
          overflow='hidden'
          sx={{
            width: { xs: "90%", md: 1250 },
            height: 850,
          }}
          boxShadow={theme.palette.main.shadow.secondary}
          borderRadius={2}
        >
          <Box className={classes.titleContainer}>
            <Typography className={classes.title}>{title}</Typography>
            <CloseIcon
              style={{ color: theme.palette.natural.t2, cursor: "pointer" }}
              onClick={onClose}
            />
          </Box>
          <Stack
            direction='row'
            sx={{
              width: 1,
              gap: 3,
              height: 1,
            }}
          >
            <Stack
              sx={{
                minWidth: 613,
                maxHeight: 850,
                backgroundColor: theme.palette.natural.t9,
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 130,
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <Button
                  variant='outlined'
                  onClick={() => setPreviewModalOpen(true)}
                >
                  {t("campaign.preview")}
                </Button>
              </Box>
              <NotificationPreview notificationHtml={notificationHtml} />
              {(isNewNotification || detectedChange) && (
                <Box
                  sx={{
                    width: 130,
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 3,
                      position: "relative",
                      zIndex: theme.zIndex.appBar,
                    }}
                  >
                    {detectedChange && (
                      <RestartAltIcon
                        sx={{ cursor: "pointer" }}
                        onClick={onReset}
                      />
                    )}
                    <CustomButton
                      size='small'
                      type='primary'
                      title={t("saveChanges")}
                      style={
                        {
                          padding: "0px 8px",
                          textWrap: "nowrap",
                        } as any
                      }
                      onClick={onSave}
                      isLoading={isLoading}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
            <Stack
              sx={{
                width: 1,
                height: 1,
              }}
            >
              {children}
            </Stack>
          </Stack>
        </Stack>
      </ModalBase>
      <BrandingPreview
        modalOpen={previewModalOpen}
        setModalOpen={setPreviewModalOpen}
      >
        <CloseIcon
          sx={{
            position: "absolute",
            top: 10,
            right: 20,
            cursor: "pointer",
          }}
          onClick={() => setPreviewModalOpen(false)}
        />
        <Box
          style={{
            height: 160,
            position: "absolute",
            bottom: 0,
            left: 15,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NotificationPreview
            notificationHtml={notificationHtml}
            backgroundColor='transparent'
          />
        </Box>
      </BrandingPreview>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  titleContainer: {
    height: 34,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: theme.palette.main.secondary.u1,
  },
  icon: {
    color: theme.palette.main.alert.o,
    fontSize: 24,
    cursor: "pointer",
  },
}));
